import { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { CheckCircle2, XCircle, BarChart3, RefreshCw, Bug } from 'lucide-react';

const JobProgressContext = createContext();

export function JobProgressProvider({ children }) {
  const [socket, setSocket] = useState(null);
  const [activeJobs, setActiveJobs] = useState({});
  const [previousStatuses, setPreviousStatuses] = useState({});

  useEffect(() => {
    // Connect to WebSocket
    const newSocket = io(window.location.origin, {
      transports: ['websocket', 'polling'],
      path: '/socket.io/',
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    newSocket.on('connect', () => {
      console.log('Connected to job progress WebSocket', newSocket.id);
    });

    newSocket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message);
    });

    newSocket.on('jobs', (jobs) => {
      const jobsMap = {};
      jobs.forEach(job => {
        jobsMap[job.id] = job;
      });
      setActiveJobs(jobsMap);
    });

    newSocket.on('progress', (jobId, jobData) => {
      // Only log progress for non-enrichment jobs to reduce spam
      const isEnrichmentJob = jobId?.startsWith('enrichment-');
      const isExtractionJob = jobId?.startsWith('extraction-');
      if (!isEnrichmentJob && !isExtractionJob) {
        console.log('Progress update:', jobId, jobData.progress + '%', jobData.messages[jobData.messages.length - 1]?.message);
      }
      setActiveJobs(prev => ({
        ...prev,
        [jobId]: { id: jobId, ...jobData }
      }));
    });

    newSocket.on('job-update', ({ jobId, ...jobData }) => {
      const isEnrichmentJob = jobId?.startsWith('enrichment-');
      const isExtractionJob = jobId?.startsWith('extraction-');
      
      // Completely silence enrichment and extraction jobs - they're background processes and too noisy
      // Only update state, don't log anything for these jobs
      if (isEnrichmentJob || isExtractionJob) {
        setActiveJobs(prev => ({
          ...prev,
          [jobId]: { id: jobId, ...jobData }
        }));
        return; // Exit early, don't log
      }
      
      // For other jobs, only log if status changed
      const previousStatus = previousStatuses[jobId];
      const statusChanged = previousStatus !== jobData.status;
      
      if (statusChanged) {
        console.log('Job update:', jobId, jobData.status);
      }
      
      // Update previous status
      setPreviousStatuses(prev => ({
        ...prev,
        [jobId]: jobData.status
      }));
      
      setActiveJobs(prev => ({
        ...prev,
        [jobId]: { id: jobId, ...jobData }
      }));
    });

    newSocket.on('debug-message', ({ jobId, message, timestamp }) => {
      console.log(`[${jobId}] ${message}`);
      setActiveJobs(prev => {
        const existing = prev[jobId] || { debugMessages: [] };
        return {
          ...prev,
          [jobId]: {
            ...existing,
            debugMessages: [...(existing.debugMessages || []), message]
          }
        };
      });
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from job progress WebSocket');
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  const subscribeToJob = (jobId) => {
    if (socket) {
      socket.emit('subscribe', jobId);
    }
  };

  const getJob = (jobId) => {
    return activeJobs[jobId] || null;
  };

  return (
    <JobProgressContext.Provider value={{ activeJobs, subscribeToJob, getJob }}>
      {children}
    </JobProgressContext.Provider>
  );
}

export function useJobProgress() {
  return useContext(JobProgressContext);
}

